import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AdminRequest, RequestStatus, listAdminRequests } from '../../api/marketplace';
import {
  STATUS_LABELS,
  formatCountries,
  formatDateTime,
  serviceLabel,
  statusLabel,
  toDate,
} from './marketplaceFormat';
import './Marketplace.css';

type Tab = RequestStatus | 'closed';

const TABS: Tab[] = ['DETECTED', 'RELEASED', 'OPEN', 'AWAITING_CUSTOMER', 'AWARDED', 'closed'];

// The admin list endpoint filters by a single status, so the Closed tab merges
// every terminal status (and in-house claims, which leave the marketplace).
export const CLOSED_STATUSES: RequestStatus[] = [
  'CLAIMED_INHOUSE',
  'COMPLETED',
  'DISMISSED',
  'EXPIRED',
  'CANCELLED',
];

const tabLabel = (tab: Tab): string => (tab === 'closed' ? 'Closed' : STATUS_LABELS[tab]);

// When the customer asked; older requests only have createdAt.
const requestedTime = (item: AdminRequest): number =>
  toDate(item.requestedAt || item.createdAt)?.getTime() || 0;

const byRequestedDesc = (a: AdminRequest, b: AdminRequest): number => requestedTime(b) - requestedTime(a);

const AdminRequestsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState<Tab>('DETECTED');
  const [requests, setRequests] = useState<AdminRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const statuses = tab === 'closed' ? CLOSED_STATUSES : [tab];
      const lists = await Promise.all(statuses.map(status => listAdminRequests(token, status)));
      setRequests(lists.flat().sort(byRequestedDesc));
    } catch (err) {
      setRequests([]);
      setError(err instanceof Error ? err.message : 'Failed to load marketplace requests.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, tab]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="marketplace-page">
      <div className="task-view-header">
        <h2>Marketplace requests</h2>
        <div className="marketplace-actions">
          <Link to="/marketplace/admin/firms" className="service-link">Firms</Link>
          <button type="button" className="download-button" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>
      <div className="tabs" role="tablist">
        {TABS.map(item => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={tab === item}
            className={tab === item ? 'active' : ''}
            onClick={() => setTab(item)}
          >
            {tabLabel(item)}
          </button>
        ))}
      </div>
      {loading ? (
        <p>Loading requests...</p>
      ) : error ? (
        <div className="status-error-banner" role="alert">{error}</div>
      ) : requests.length === 0 ? (
        <p className="marketplace-empty">No requests in this tab.</p>
      ) : (
        <table className="marketplace-table">
          <thead>
            <tr>
              <th>Requested</th>
              <th>Service</th>
              <th>Countries</th>
              <th>Customer</th>
              <th>Source</th>
              {tab === 'closed' && <th>Status</th>}
              <th>Summary</th>
            </tr>
          </thead>
          <tbody>
            {requests.map(item => (
              <tr key={item.id}>
                <td>
                  <Link to={`/marketplace/admin/requests/${item.id}`} className="service-link">
                    {formatDateTime(item.requestedAt || item.createdAt)}
                  </Link>
                </td>
                <td>{serviceLabel(item.serviceType)}</td>
                <td>{formatCountries(item.countries)}</td>
                <td>{item.customerName || item.customerEmail || 'n/a'}</td>
                <td>{item.source}</td>
                {tab === 'closed' && <td>{statusLabel(item.status)}</td>}
                <td className="marketplace-summary-cell" title={item.anonymisedSummary}>
                  {item.anonymisedSummary}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminRequestsPage;
